
import { Component, Watch } from "vue-property-decorator";
import {
  getClientDeviceSettingsDeprecated,
} from "@/data/repository/ClientRepository";
import { createDefaultImportConfiguration, GenericDeviceSettingsDeprecated } from "@/data/models/Client";
import { AllDevicesSettings, ImportConfiguration } from "@/data/models/Settings";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import { AllDevicesSettingsModule } from "@/store/modules/AllDevicesSettingsModule";
import { defineOwnAllDevicesSettingsClientAccess } from "@/permissions/ClientPermissions";
import { updateAllDevicesSettings } from "@/data/repository/SettingsRepository";
import PickerDialogComponent from "@/components/common/PickerDialog.vue";
import SettingsPageMixin from "@/components/settings/SettingsPageMixin";

@Component({
  components: {
    "confirmation-dialog": ConfirmationDialogComponent,
    picker: PickerDialogComponent,
  },
})
export default class DeviceSettings extends SettingsPageMixin {
  isInitializing = true;

  clientSettings: GenericDeviceSettingsDeprecated | null = null;
  newCategoryAllowed = false;
  newTemplateAllowed = false;
  isCreateAllowed = false;
  updateAllowed = false;
  yesNoValues = [true, false];

  initialImportConfiguration: ImportConfiguration = createDefaultImportConfiguration();

  @Watch("isCreateAllowed")
  onIsCreateAllowedChanged() {
    this.newCategoryAllowed = this.isCreateAllowed;
    this.newTemplateAllowed = this.isCreateAllowed;
  }

  @Watch("allDevicesSettings")
  onAllDevicesSettingsChanged() {
    const importConfiguration = this.allDevicesSettings.importConfiguration;

    if (
      importConfiguration && 
      !importConfiguration.defaultImportConfiguration
    ) {
      this.initializeParameters(importConfiguration);  
    }
  }

  get hasChanged() {
    return (
      this.newCategoryAllowed !== this.initialImportConfiguration.newCategoryAllowed ||
      this.newTemplateAllowed !== this.initialImportConfiguration.newTemplateAllowed ||
      this.updateAllowed !== this.initialImportConfiguration.updateAllowed
    );
  }

  get canEdit() {
    return defineOwnAllDevicesSettingsClientAccess();
  }

  get allDevicesSettings() {
    return AllDevicesSettingsModule.allDevicesSettings;
  }

  get readonly() {
    return !this.isEditMode || !this.canEdit;
  }

  get formatYesNo() {
    return (value: boolean) => {
      return this.$t(`common.${value ? "yes" : "no"}`).toString();
    };
  }

  initializeParameters(
    importConfiguration: ImportConfiguration,
  ) {
    this.newCategoryAllowed = importConfiguration.newCategoryAllowed;
    this.newTemplateAllowed = importConfiguration.newTemplateAllowed;
    this.updateAllowed = importConfiguration.updateAllowed;

    this.isCreateAllowed = this.newCategoryAllowed || this.newTemplateAllowed;

    this.initialImportConfiguration = importConfiguration;

    this.isEditMode = false;
  }

  async created() {
    this.isInitializing = true;
    const importConfiguration = this.allDevicesSettings.importConfiguration;

    if (importConfiguration && !importConfiguration.defaultImportConfiguration) {
      this.initializeParameters(importConfiguration);
    }
    else {
      this.clientSettings = await getClientDeviceSettingsDeprecated();
      const defaultImportConfiguration = createDefaultImportConfiguration();

      if (this.clientSettings?.newCategoryAllowed) {
        defaultImportConfiguration.newCategoryAllowed = this.clientSettings.newCategoryAllowed;
      }

      if (this.clientSettings?.newTemplateAllowed) {
        defaultImportConfiguration.newTemplateAllowed = this.clientSettings.newTemplateAllowed;
      }

      if (this.clientSettings?.updateAllowed) {
        defaultImportConfiguration.updateAllowed = this.clientSettings.updateAllowed;
      }

      this.initializeParameters(defaultImportConfiguration);
    }
    this.isInitializing = false;
  }

  async doSave() {
    const importConfigurationUpdate: ImportConfiguration = {
      newCategoryAllowed: this.newCategoryAllowed,
      newTemplateAllowed: this.newTemplateAllowed,
      updateAllowed: this.updateAllowed,
      
      defaultImportConfiguration: false,
    }

    const update: AllDevicesSettings = {
      ...this.allDevicesSettings,
      importConfiguration: importConfigurationUpdate,
    }

    await updateAllDevicesSettings(update);

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.initialImportConfiguration = update.importConfiguration!!;
  }

  mounted() {
    this.setToolbarTitle(this.$t("settings.hyDryDevice.configurationsImportExport").toString());
  }
}
